import { h, Fragment } from 'preact'
import { useMemo, useState, useRef, useEffect } from 'preact/hooks'
import PropTypes from 'prop-types'

import { useHierarchicalNetworkMembers } from 'lib/hierarchicalNetworkMembersHooks'

import Link from 'components/Link'
import Icon from 'components/Icon'
import OrganizationIcon from 'components/OrganizationIcon'
import SingleLine from 'components/SingleLine'
import Spinner from 'components/Spinner'

import './index.sass'

const COMPONENT_NAME = 'OrganizationConstellationNav'

export default function OrganizationConstellationNav({ organizationApikey, organization }) {
  if (!organization.is_network && organization.network_type !== 'hierarchical') {
    return null
  }

  return <OrganizationConstellationNavContent {...{ organizationApikey, organization }} />
}

function OrganizationConstellationNavContent({ organizationApikey }) {
  const {
    members,
    relationships,
    hierarchicalNetworkMembersLoading: loading,
    hierarchicalNetworkMembersLoadingError: error,
  } = useHierarchicalNetworkMembers(organizationApikey, COMPONENT_NAME)

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef()

  useEffect(() => {
    if (!dropdownOpen) return
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [dropdownOpen])

  const { breadcrumbs, children, siblings, currentMember, parentMember } = useMemo(() => {
    if (!members || members.length === 0) {
      return { breadcrumbs: [], children: [], siblings: [], currentMember: null, parentMember: null }
    }

    const memberMap = {}
    members.forEach(m => { memberMap[m.apikey] = m })

    // Build parent-child maps from relationships (camelCase from backend)
    const parentOf = {}
    const childrenOf = {}
    relationships.forEach(rel => {
      const parent = rel.parentApikey
      const child = rel.childApikey
      if (parent && child) {
        parentOf[child] = parent
        if (!childrenOf[parent]) childrenOf[parent] = []
        childrenOf[parent].push(child)
      }
    })

    // Also use parent_organization_apikey from member data as fallback
    members.forEach(m => {
      if (m.parent_organization_apikey && !parentOf[m.apikey]) {
        parentOf[m.apikey] = m.parent_organization_apikey
        if (!childrenOf[m.parent_organization_apikey]) childrenOf[m.parent_organization_apikey] = []
        if (!childrenOf[m.parent_organization_apikey].includes(m.apikey)) {
          childrenOf[m.parent_organization_apikey].push(m.apikey)
        }
      }
    })

    const currentMember = memberMap[organizationApikey] || null

    // Build breadcrumb trail from current up to root
    const trail = []
    let current = organizationApikey
    const visited = new Set()
    while (current && memberMap[current] && !visited.has(current)) {
      visited.add(current)
      trail.unshift(memberMap[current])
      current = parentOf[current]
    }

    if (trail.length === 0 && currentMember) {
      trail.push(currentMember)
    }

    // Find direct children of current hub
    const myChildren = (childrenOf[organizationApikey] || [])
      .filter(key => memberMap[key])
      .map(key => memberMap[key])
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    // Find siblings (same parent, excluding current)
    const myParent = parentOf[organizationApikey]
    let mySiblings = []
    if (myParent && childrenOf[myParent]) {
      mySiblings = childrenOf[myParent]
        .filter(key => key !== organizationApikey && memberMap[key])
        .map(key => memberMap[key])
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }

    const parentMember = myParent ? memberMap[myParent] || null : null

    return {
      breadcrumbs: trail,
      children: myChildren,
      siblings: mySiblings,
      currentMember,
      parentMember,
    }
  }, [members, relationships, organizationApikey])

  if (loading) return <div className="OrganizationConstellationNav OrganizationConstellationNav--loading">
    <Spinner />
  </div>
  if (error || !currentMember) return null

  const hasDropdownContent = children.length > 0 || siblings.length > 0 || parentMember
  const totalRelated = children.length + siblings.length

  if (breadcrumbs.length <= 1 && !hasDropdownContent) return null

  return <div className="OrganizationConstellationNav">
    <div className="OrganizationConstellationNav-breadcrumbs">
      <Icon type="treeStructure" size="sm" className="OrganizationConstellationNav-icon" />
      {breadcrumbs.map((member, index) => {
        const isLast = index === breadcrumbs.length - 1
        return <Fragment key={member.apikey}>
          {index > 0 && <span className="OrganizationConstellationNav-separator">
            <Icon type="chevronRight" size="xs" />
          </span>}
          {isLast
            ? <span className="OrganizationConstellationNav-crumb OrganizationConstellationNav-crumb--current">
              <OrganizationIcon organization={member} size="sm" />
              <SingleLine>{member.name || member.apikey}</SingleLine>
            </span>
            : <Link
              href={`/${member.apikey}`}
              className="OrganizationConstellationNav-crumb"
            >
              <OrganizationIcon organization={member} size="sm" />
              <SingleLine>{member.name || member.apikey}</SingleLine>
            </Link>
          }
        </Fragment>
      })}
    </div>

    {hasDropdownContent && (
      <div className="OrganizationConstellationNav-switcher" ref={dropdownRef}>
        <button
          className="OrganizationConstellationNav-switcher-toggle"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <Icon type="network" size="sm" />
          <span>Navigate</span>
          {totalRelated > 0 && (
            <span className="OrganizationConstellationNav-switcher-count">{totalRelated}</span>
          )}
          <Icon type={dropdownOpen ? 'closeChevron' : 'openChevron'} size="xs" />
        </button>

        {dropdownOpen && (
          <div className="OrganizationConstellationNav-dropdown">
            {/* Parent Hub - go up */}
            {parentMember && (
              <Link
                href={`/${parentMember.apikey}`}
                className="OrganizationConstellationNav-dropdown-item OrganizationConstellationNav-dropdown-item--parent"
                onClick={() => setDropdownOpen(false)}
              >
                <Icon type="closeChevron" size="xs" />
                <OrganizationIcon organization={parentMember} size="sm" />
                <div className="OrganizationConstellationNav-dropdown-item-info">
                  <span className="OrganizationConstellationNav-dropdown-item-name">
                    <SingleLine>{parentMember.name || parentMember.apikey}</SingleLine>
                  </span>
                  <span className="OrganizationConstellationNav-dropdown-item-role">Parent Hub</span>
                </div>
              </Link>
            )}

            {/* Child Hubs - go down */}
            {children.length > 0 && (
              <Fragment>
                <div className="OrganizationConstellationNav-dropdown-label">
                  Child Hubs ({children.length})
                </div>
                {children.map(member =>
                  <DropdownItem
                    key={member.apikey}
                    member={member}
                    onClose={() => setDropdownOpen(false)}
                  />
                )}
              </Fragment>
            )}

            {/* Sibling Hubs - go sideways */}
            {siblings.length > 0 && (
              <Fragment>
                <div className="OrganizationConstellationNav-dropdown-label">
                  Sibling Hubs ({siblings.length})
                </div>
                {siblings.map(member =>
                  <DropdownItem
                    key={member.apikey}
                    member={member}
                    onClose={() => setDropdownOpen(false)}
                  />
                )}
              </Fragment>
            )}
          </div>
        )}
      </div>
    )}
  </div>
}

function DropdownItem({ member, onClose }) {
  return <Link
    href={`/${member.apikey}`}
    className="OrganizationConstellationNav-dropdown-item"
    onClick={onClose}
  >
    <OrganizationIcon organization={member} size="sm" />
    <div className="OrganizationConstellationNav-dropdown-item-info">
      <span className="OrganizationConstellationNav-dropdown-item-name">
        <SingleLine>{member.name || member.apikey}</SingleLine>
      </span>
      {member.tag_line && (
        <span className="OrganizationConstellationNav-dropdown-item-tagline">
          <SingleLine>{member.tag_line}</SingleLine>
        </span>
      )}
      {member.hasChildren && (
        <span className="OrganizationConstellationNav-dropdown-item-hasChildren">
          Has sub-hubs
        </span>
      )}
    </div>
    {member.hasChildren && <Icon type="chevronRight" size="xs" />}
  </Link>
}

OrganizationConstellationNav.propTypes = {
  organizationApikey: PropTypes.string.isRequired,
  organization: PropTypes.object.isRequired,
}
